'use client';

import { useContext } from 'react';
import {
  browserName,
  browserVersion,
  deviceType,
  mobileModel,
  osName,
  osVersion,
  mobileVendor,
} from 'react-device-detect';
import { datadogRum } from '@datadog/browser-rum';
import type { Campaign } from '@segment/analytics-core';

import { retry } from '../../utils/functions.shared';
import { useApplicationStore } from '../../stores/application.store';

import { AnalyticsContext } from './context';

export function useAnalytics() {
  const { analytics } = useContext(AnalyticsContext);
  const campaign = useApplicationStore((state) => state.campaign);

  return {
    track: (...args: Parameters<NonNullable<typeof analytics>['track']>) => {
      const [name, properties, context] = args;
      const updatedProperties: typeof properties = {
        campaign_name: campaign?.name ?? null,
        device_type: deviceType,
        screen_height: String(window.screen.height),
        screen_width: String(window.screen.width),
        ...properties,
      };

      void analytics?.addSourceMiddleware(({ payload, next }) => {
        const utmData = window.sessionStorage.getItem('campaign');
        if (payload.obj.context && utmData) {
          payload.obj.context.campaign = JSON.parse(utmData) as Campaign;
        }

        next(payload);
      });

      retry(async () =>
        analytics?.track(name, updatedProperties, {
          device: {
            type: deviceType,
            model: mobileModel,
            name: mobileVendor,
          },
          os: {
            name: osName,
            version: osVersion,
          },
          screen: {
            height: String(window.screen.height),
            width: String(window.screen.width),
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          browser: {
            name: browserName,
            version: browserVersion,
          },
          ...context,
        }),
      ).catch((e: unknown) => {
        datadogRum.addError(e, {
          error_message:
            'Unable to send analytics track event. Event: ' + (typeof name === 'string' ? name : JSON.stringify(name)),
        });
      });
    },
    identify: () => {
      void analytics?.identify();
    },
    logout: async () => {
      const user = await analytics?.user();
      user?.logout();
    },
  };
}
